import { AppModal } from '@Common/AppModal';
import { Spinner } from '@Common/Spinner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SetStateType } from 'commonTypes';
import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useCancelSubscription } from 'store/subscription/actions';
import { SubscriptionCustomerType } from 'store/subscription/types';

import { UpgradeSubscriptionModal } from './UpgradeSubscriptionModal';

interface CancelSubscriptionModalProps {
  open: boolean;
  setOpen: SetStateType<boolean>;
  date: number | undefined;
  subscription?: SubscriptionCustomerType;
}

export const CancelSubscriptionModal = ({
  open,
  setOpen,
  date,
  subscription,
}: CancelSubscriptionModalProps) => {
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const { mutate: cancelSubscription, isPending } = useCancelSubscription();
  const closeModal = () => {
    setOpen(false);
  };

  return (
    <>
      <AppModal open={open}>
        <div className="flex w-full items-center justify-between mb-3">
          <h1>
            <FormattedMessage id="cancelSubscription" />
          </h1>
          <XMarkIcon
            onClick={closeModal}
            className="h-6 w-6 cursor-pointer text-gray-500 md:hover:text-gray-700"
          />
        </div>

        <div className="flex items-center justify-center flex-col">
          <div className="text-center">
            <FormattedMessage id="cancelSubscriptionTitle" />
          </div>
          {date && (
            <div className="mb-4 text-center">
              <FormattedMessage
                id="cancelStillAvailable"
                values={{
                  date: (
                    <strong>
                      <FormattedDate
                        value={date * 1000}
                        year="numeric"
                        month="long"
                        day="2-digit"
                      />
                    </strong>
                  ),
                }}
              />
            </div>
          )}

          <div className="flex flex-col w-full items-center justify-center mb-4 bg-gray-100 rounded-lg p-4">
            <button
              onClick={() => {
                cancelSubscription(undefined, { onSuccess: closeModal });
              }}
              type="button"
              className="rounded-md bg-gray-700 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 w-full"
            >
              {isPending ? <Spinner /> : <FormattedMessage id="finishCancelation" />}
            </button>
            <div className="mt-2 text-sm text-center">
              <FormattedMessage id="finishCancelationSub" />
            </div>
          </div>

          <div className="flex flex-col w-full items-center justify-center bg-gray-100 rounded-lg p-4">
            <button
              type="button"
              onClick={() => {
                closeModal();
                setOpenUpgradeModal(true);
              }}
              className="w-full rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <FormattedMessage id="changeToAnotherTier" />
            </button>
            <div className="mt-2 text-sm text-center">
              <FormattedMessage id="changeToAnotherTierSub" />
            </div>
          </div>
        </div>
      </AppModal>
      <UpgradeSubscriptionModal
        open={openUpgradeModal}
        setOpen={setOpenUpgradeModal}
        isCanceled={subscription?.status.toLocaleLowerCase() === 'cancel'}
        subscription={subscription}
        setOpenCancelSubscriptionModal={setOpen}
      />
    </>
  );
};
